import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { SSOProvider, Role } from '../../types';
import { getSSOProvider, updateSSOProvider, getRoles } from '../../utils/auth/authService';
import SSOProviderViewMode from '../../components/auth/SSOProviderViewMode';
import SSOProviderEditMode from '../../components/auth/SSOProviderEditMode';

type Mode = 'view' | 'edit';

const SSOProviderDetailsPage: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('view');
  const [provider, setProvider] = useState<SSOProvider | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editDefaultRole, setEditDefaultRole] = useState<string | undefined>(undefined);
  const [editAutoCreateUsers, setEditAutoCreateUsers] = useState(true);
  
  // OIDC config
  const [editOidcClientId, setEditOidcClientId] = useState('');
  const [editOidcClientSecret, setEditOidcClientSecret] = useState('');
  const [editOidcDiscoveryUrl, setEditOidcDiscoveryUrl] = useState('');
  const [editOidcScopes, setEditOidcScopes] = useState('');
  
  // SAML config
  const [editSamlEntityId, setEditSamlEntityId] = useState('');
  const [editSamlSsoUrl, setEditSamlSsoUrl] = useState('');
  const [editSamlSloUrl, setEditSamlSloUrl] = useState('');
  const [editSamlX509Cert, setEditSamlX509Cert] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      const [providerData, rolesData] = await Promise.all([
        getSSOProvider(id),
        getRoles()
      ]);
      setProvider(providerData);
      setRoles(rolesData);
      
      // Initialize edit state
      setEditName(providerData.name);
      setEditEnabled(providerData.enabled);
      setEditDefaultRole(providerData.default_role);
      setEditAutoCreateUsers(providerData.auto_create_users);
      
      if (providerData.provider_type === 'oidc' && providerData.oidc_config) {
        setEditOidcClientId(providerData.oidc_config.client_id);
        setEditOidcClientSecret(providerData.oidc_config.client_secret);
        setEditOidcDiscoveryUrl(providerData.oidc_config.discovery_url);
        setEditOidcScopes(providerData.oidc_config.scopes.join(', '));
      }
      
      if (providerData.provider_type === 'saml' && providerData.saml_config) {
        setEditSamlEntityId(providerData.saml_config.entity_id);
        setEditSamlSsoUrl(providerData.saml_config.sso_url);
        setEditSamlSloUrl(providerData.saml_config.slo_url || '');
        setEditSamlX509Cert(providerData.saml_config.x509_cert);
      }
    } catch (err) {
      setError('Failed to load SSO provider');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleCancel = () => {
    if (provider) {
      setEditName(provider.name);
      setEditEnabled(provider.enabled);
      setEditDefaultRole(provider.default_role);
      setEditAutoCreateUsers(provider.auto_create_users);
      
      if (provider.provider_type === 'oidc' && provider.oidc_config) {
        setEditOidcClientId(provider.oidc_config.client_id);
        setEditOidcClientSecret(provider.oidc_config.client_secret);
        setEditOidcDiscoveryUrl(provider.oidc_config.discovery_url);
        setEditOidcScopes(provider.oidc_config.scopes.join(', '));
      }
      
      if (provider.provider_type === 'saml' && provider.saml_config) {
        setEditSamlEntityId(provider.saml_config.entity_id);
        setEditSamlSsoUrl(provider.saml_config.sso_url);
        setEditSamlSloUrl(provider.saml_config.slo_url || '');
        setEditSamlX509Cert(provider.saml_config.x509_cert);
      }
    }
    setError('');
    setMode('view');
  };

  const handleSave = async () => {
    if (!id || !provider) return;

    setSaving(true);
    setError('');

    try {
      const updates: any = {
        name: editName,
        enabled: editEnabled,
        default_role: editDefaultRole || undefined,
        auto_create_users: editAutoCreateUsers,
      };
      
      if (provider.provider_type === 'oidc') {
        updates.oidc_config = {
          client_id: editOidcClientId,
          client_secret: editOidcClientSecret,
          discovery_url: editOidcDiscoveryUrl,
          scopes: editOidcScopes.split(',').map(s => s.trim()).filter(Boolean),
          attribute_mapping: provider.oidc_config?.attribute_mapping || {
            email: 'email',
            first_name: 'given_name',
            last_name: 'family_name',
          },
        };
      }
      
      if (provider.provider_type === 'saml') {
        updates.saml_config = {
          entity_id: editSamlEntityId,
          sso_url: editSamlSsoUrl,
          slo_url: editSamlSloUrl || undefined,
          x509_cert: editSamlX509Cert,
          attribute_mapping: provider.saml_config?.attribute_mapping || {
            email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
            first_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
            last_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
          },
        };
      }
      
      const updatedProvider = await updateSSOProvider(id, updates);
      setProvider(updatedProvider);
      setMode('view');
    } catch (err) {
      setError('Failed to save SSO provider');
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading...
      </div>
    );
  }

  if (!provider) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: theme.text, marginBottom: 16 }}>
          SSO Provider not found
        </div>
        <button
          onClick={() => navigate('/auth/sso-providers')}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Back to SSO Providers
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/auth/sso-providers')}
            style={{
              padding: 8,
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>
            {provider.name}
          </h1>
          <span style={{
            padding: '4px 8px',
            background: provider.provider_type === 'oidc' ? '#e0f2fe' : '#f0e6ff',
            color: provider.provider_type === 'oidc' ? '#0369a1' : '#7c3aed',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
          }}>
            {provider.provider_type}
          </span>
          {provider.enabled ? (
            <span style={{
              padding: '4px 8px',
              background: '#d1fae5',
              color: '#065f46',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
            }}>
              Enabled
            </span>
          ) : (
            <span style={{
              padding: '4px 8px',
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
            }}>
              Disabled
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {mode === 'view' ? (
            <button
              onClick={handleEdit}
              style={{
                padding: '10px 20px',
                background: theme.buttonBackground,
                color: theme.buttonText,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 500,
              }}
            >
              <Edit size={18} />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <X size={18} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  background: theme.buttonBackground,
                  color: theme.buttonText,
                  border: 'none',
                  borderRadius: 4,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
        }}>
          {error}
        </div>
      )}

      {/* Content */}
      {mode === 'view' ? (
        <SSOProviderViewMode provider={provider} roles={roles} theme={theme} />
      ) : (
        <SSOProviderEditMode
          provider={provider}
          roles={roles}
          theme={theme}
          editName={editName}
          editEnabled={editEnabled}
          editDefaultRole={editDefaultRole}
          editAutoCreateUsers={editAutoCreateUsers}
          editOidcClientId={editOidcClientId}
          editOidcClientSecret={editOidcClientSecret}
          editOidcDiscoveryUrl={editOidcDiscoveryUrl}
          editOidcScopes={editOidcScopes}
          editSamlEntityId={editSamlEntityId}
          editSamlSsoUrl={editSamlSsoUrl}
          editSamlSloUrl={editSamlSloUrl}
          editSamlX509Cert={editSamlX509Cert}
          onNameChange={setEditName}
          onEnabledChange={setEditEnabled}
          onDefaultRoleChange={setEditDefaultRole}
          onAutoCreateUsersChange={setEditAutoCreateUsers}
          onOidcClientIdChange={setEditOidcClientId}
          onOidcClientSecretChange={setEditOidcClientSecret}
          onOidcDiscoveryUrlChange={setEditOidcDiscoveryUrl}
          onOidcScopesChange={setEditOidcScopes}
          onSamlEntityIdChange={setEditSamlEntityId}
          onSamlSsoUrlChange={setEditSamlSsoUrl}
          onSamlSloUrlChange={setEditSamlSloUrl}
          onSamlX509CertChange={setEditSamlX509Cert}
        />
      )}
    </div>
  );
};

export default SSOProviderDetailsPage;
